/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../firebase";

const FUNCTIONS_REGION = "southamerica-east1";
const TYPES = ["taxonomic_change", "taxonomic_split"];

function displayedDate(value) {
  if (!value?.toDate && !value?.seconds) return "";
  const date =
    typeof value.toDate === "function"
      ? value.toDate()
      : new Date(value.seconds * 1000);
  return date.toLocaleDateString();
}

function NoticeContent({ notice, photoPage, photoBusy, photoError, loadMore }) {
  const isSplit = notice.type === "taxonomic_split";
  const sources = Array.isArray(notice.sourceNames) ? notice.sourceNames : [];
  const species = Array.isArray(notice.resultingSpecies)
    ? notice.resultingSpecies
    : [];
  return (
    <div className="space-y-3 text-sm leading-relaxed text-[#374049]">
      <p>{notice.message}</p>
      {sources.length > 0 && (
        <p className="text-xs text-[#374049]/75">
          <strong>
            Previous scientific name{sources.length > 1 ? "s" : ""}:{" "}
          </strong>
          <em>{sources.join(", ")}</em>
        </p>
      )}
      {!isSplit && notice.revisionType === "common_name_change" && (
        <p className="text-xs text-[#374049]/75">
          <strong>Common name: </strong>
          {notice.previousCommonName ? `${notice.previousCommonName} → ` : ""}
          {notice.acceptedCommonName}
        </p>
      )}
      {!isSplit && notice.revisionType !== "common_name_change" && (
        <p className="text-xs text-[#374049]/75">
          <strong>Accepted scientific name: </strong>
          <em>{notice.acceptedScientificName}</em>
          {notice.acceptedCommonName ? ` (${notice.acceptedCommonName})` : ""}
        </p>
      )}
      {!isSplit && (
        <section
          className="rounded-2xl border border-[#93CA87]/50 bg-white p-3 sm:p-4"
          aria-label="Affected photographs and taxonomic names"
        >
          <h3 className="text-base font-semibold">
            Affected photographs (
            {photoPage?.total ?? notice.affectedPhotographs ?? 0})
          </h3>
          <p className="mt-1 text-xs text-[#374049]/65">
            These are the actual photographs affected by this update, taken from
            the original observation backup.
          </p>
          <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 rounded-xl bg-[#FCFFF6] p-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#374049]/60">
                Before
              </p>
              <p className="break-words font-semibold">
                <em>{sources.join(", ")}</em>
              </p>
              {notice.revisionType === "common_name_change" &&
                notice.previousCommonName && (
                  <p className="text-xs">{notice.previousCommonName}</p>
                )}
            </div>
            <span className="text-lg text-[#01767A]" aria-hidden="true">
              →
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#374049]/60">
                After
              </p>
              <p className="break-words font-semibold">
                <em>{notice.acceptedScientificName || sources.join(", ")}</em>
              </p>
              {notice.acceptedCommonName && (
                <p className="text-xs">{notice.acceptedCommonName}</p>
              )}
            </div>
          </div>
          {photoPage?.photos?.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {photoPage.photos.map((photo) => (
                <div
                  key={`${photo.sourceScientificName}-${photo.position}`}
                  className="min-w-0 overflow-hidden rounded-xl border border-[#93CA87]/40 bg-[#FCFFF6]"
                >
                  <div className="flex aspect-square items-center justify-center bg-[#374049]/5">
                    {photo.url ? (
                      <img
                        src={photo.url}
                        alt={`Affected photograph ${photo.position + 1}: ${photo.sourceScientificName}`}
                        loading="lazy"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <span className="px-3 text-center text-xs text-[#374049]/60">
                        Image unavailable
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 p-2 text-[11px] leading-snug">
                    <p className="font-semibold">
                      Photograph {photo.position + 1}
                    </p>
                    <p>
                      <span className="text-[#374049]/60">Before: </span>
                      <em>{photo.sourceScientificName}</em>
                    </p>
                    <p>
                      <span className="text-[#374049]/60">After: </span>
                      <em>
                        {notice.acceptedScientificName ||
                          photo.sourceScientificName}
                      </em>
                    </p>
                    {photo.date && (
                      <p className="text-[#374049]/70">{photo.date}</p>
                    )}
                    {photo.location && (
                      <p className="break-words text-[#374049]/70">
                        {photo.location}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {photoBusy && (
            <p className="mt-3 text-xs text-[#374049]/65" role="status">
              Loading affected photographs…
            </p>
          )}
          {photoError && (
            <p className="mt-3 text-xs text-amber-800" role="alert">
              {photoError}
            </p>
          )}
          {photoPage?.total === 0 && !photoBusy && !photoError && (
            <p className="mt-3 text-xs text-[#374049]/65">
              No photographs were associated with these records.
            </p>
          )}
          {photoPage?.nextOffset != null && (
            <button
              type="button"
              onClick={loadMore}
              disabled={photoBusy}
              className="mt-3 rounded-lg border border-[#93CA87] px-3 py-2 text-xs font-semibold text-[#01767A] disabled:opacity-50"
            >
              Show more photographs
            </button>
          )}
        </section>
      )}
      {isSplit ? (
        <>
          <p className="text-xs text-[#374049]/75">
            {notice.affectedPhotographs || 0} photograph(s) may require
            individual reidentification. You will decide which resulting species
            each photograph represents, or leave it uncertain.
          </p>
          <div className="space-y-2">
            {species.map((item, index) => (
              <div
                key={`${item.scientificName}-${index}`}
                className="rounded-xl border border-[#93CA87]/45 bg-white p-3"
              >
                <div className="font-semibold">
                  <em>{item.scientificName}</em>
                  {item.commonName ? ` (${item.commonName})` : ""}
                </div>
                {item.distribution && (
                  <p className="mt-1 text-xs">
                    <strong>Distribution: </strong>
                    {item.distribution}
                  </p>
                )}
                {item.identificationNotes && (
                  <p className="mt-1 text-xs">
                    <strong>Identification: </strong>
                    {item.identificationNotes}
                  </p>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-[#374049]/60">
            Geography is guidance only. It will not determine your
            identification.
          </p>
        </>
      ) : (
        <p className="text-xs text-[#374049]/75">
          This taxonomic change has already been applied by BioIndex. Clicking
          OK only acknowledges that you have been informed; no approval or
          reidentification is needed.
        </p>
      )}
      {notice.explanation && (
        <p className="text-xs text-[#374049]/75">
          <strong>Reason: </strong>
          {notice.explanation}
        </p>
      )}
      {(notice.referenceAuthority || notice.referenceUrl) && (
        <p className="text-xs text-[#374049]/75">
          <strong>Reference: </strong>
          {notice.referenceAuthority || "Taxonomic reference"}
          {/^https?:\/\//.test(notice.referenceUrl || "") && (
            <>
              {" "}
              <a
                className="text-[#01767A] underline"
                href={notice.referenceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Read source ↗
              </a>
            </>
          )}
        </p>
      )}
    </div>
  );
}

export default function TaxonomicNotices({ uid }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!uid) {
      setNotices([]);
      return undefined;
    }
    const ref = query(
      collection(db, "users", uid, "notifications"),
      where("type", "in", TYPES),
    );
    return onSnapshot(
      ref,
      (snapshot) => {
        setNotices(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setError("");
      },
      (err) => {
        console.error("Could not load taxonomic notices:", err);
        setError("Taxonomic notifications could not be retrieved.");
      },
    );
  }, [uid]);

  const pending = useMemo(
    () =>
      notices
        .filter((notice) =>
          notice.type === "taxonomic_change"
            ? !notice.acknowledgedAt
            : !notice.read,
        )
        .sort(
          (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0),
        ),
    [notices],
  );
  const notice = pending[0] || null;
  const [photoPage, setPhotoPage] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const activeNoticeId = notice?.id;
  const activeRevisionId = notice?.revisionId;
  const activeNoticeType = notice?.type;

  useEffect(() => {
    let cancelled = false;
    setPhotoPage(null);
    setPhotoError("");

    if (!uid || !activeNoticeId || activeNoticeType !== "taxonomic_change") {
      setPhotoBusy(false);
      return () => {
        cancelled = true;
      };
    }

    setPhotoBusy(true);

    const load = httpsCallable(
      getFunctions(undefined, FUNCTIONS_REGION),
      "getTaxonomicNoticePhotos",
    );

    load({ revisionId: activeRevisionId, offset: 0 })
      .then(({ data }) => {
        if (!cancelled) setPhotoPage(data);
      })
      .catch((err) => {
        console.error("Could not load affected taxonomic photographs:", err);
        if (!cancelled)
          setPhotoError(
            "Affected photographs could not be loaded. The taxonomic update itself has already been applied.",
          );
      })
      .finally(() => {
        if (!cancelled) setPhotoBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, activeNoticeId, activeRevisionId, activeNoticeType]);

  async function loadMorePhotos() {
    if (!notice || photoBusy || photoPage?.nextOffset == null) return;
    setPhotoBusy(true);
    setPhotoError("");
    try {
      const load = httpsCallable(
        getFunctions(undefined, FUNCTIONS_REGION),
        "getTaxonomicNoticePhotos",
      );
      const { data } = await load({
        revisionId: notice.revisionId,
        offset: photoPage.nextOffset,
      });
      setPhotoPage((previous) => ({
        ...data,
        photos: [...(previous?.photos || []), ...(data.photos || [])],
      }));
    } catch (err) {
      console.error("Could not load more taxonomic photographs:", err);
      setPhotoError(
        "Could not load more affected photographs. Please try again.",
      );
    } finally {
      setPhotoBusy(false);
    }
  }

  async function submit(callableName, navigateTo = null) {
    if (!notice || loading) return;
    setLoading(true);
    setError("");
    try {
      const callable = httpsCallable(
        getFunctions(undefined, FUNCTIONS_REGION),
        callableName,
      );
      await callable({ revisionId: notice.revisionId });
      if (navigateTo) navigate(navigateTo);
    } catch (err) {
      console.error("Taxonomic notification action failed:", err);
      setError(
        err?.message || "Could not record your response. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  if (
    !notice ||
    (notice.type === "taxonomic_split" &&
      location.pathname === notice.targetRoute)
  )
    return null;
  const isSplit = notice.type === "taxonomic_split";
  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center overflow-y-auto bg-[#374049]/75 px-3 py-5"
      role="presentation"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="taxonomic-notice-title"
        className={`w-full ${isSplit ? "max-w-xl" : "max-w-3xl"} overflow-hidden rounded-3xl border border-[#93CA87]/50 bg-[#FCFFF6] shadow-2xl`}
      >
        <header className="bg-[#374049] px-5 py-5 text-white sm:px-7">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#93CA87]">
            BioIndex · Taxonomic revision
          </p>
          <h2
            id="taxonomic-notice-title"
            className="mt-1 text-3xl"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            {isSplit
              ? "Species identification review"
              : "Your collection has been updated"}
          </h2>
          {displayedDate(notice.createdAt) && (
            <p className="mt-1 text-xs text-white/65">
              {displayedDate(notice.createdAt)}
            </p>
          )}
        </header>
        <div className="max-h-[min(65dvh,34rem)] space-y-4 overflow-y-auto px-5 py-5 sm:px-7">
          <NoticeContent
            notice={notice}
            photoPage={photoPage}
            photoBusy={photoBusy}
            photoError={photoError}
            loadMore={loadMorePhotos}
          />
          {error && (
            <p
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700"
            >
              {error}
            </p>
          )}
        </div>
        <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-[#93CA87]/35 bg-white px-5 py-4 sm:px-7">
          {isSplit ? (
            <>
              <button
                type="button"
                onClick={() => submit("dismissTaxonomicSplitNotice")}
                disabled={loading}
                className="rounded-xl border border-[#93CA87]/50 px-4 py-2.5 text-sm font-semibold text-[#374049] disabled:opacity-50"
              >
                {loading ? "Saving..." : "Not now"}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() =>
                  submit(
                    "dismissTaxonomicSplitNotice",
                    notice.targetRoute || "/taxonomy/reviews",
                  )
                }
                className="rounded-xl bg-[#01767A] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                Review photographs
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => submit("acknowledgeTaxonomicNotice")}
              disabled={loading}
              className="min-w-24 rounded-xl bg-[#01767A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#374049] disabled:opacity-50"
            >
              {loading ? "Saving..." : "OK"}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
}
